import './bootstrap';
import '../css/app.css';

import {hydrateRoot} from 'react-dom/client'
import {createInertiaApp, router} from '@inertiajs/react';
import {resolvePageComponent} from 'laravel-vite-plugin/inertia-helpers';
import * as Sentry from "@sentry/react";
import {AnalyticsEvent, sendEvent} from "@/services/analytics";
import axios, {AxiosResponse} from "axios";
import {config} from "@fortawesome/fontawesome-svg-core";
import {ConfirmProvider} from "@/Components/ConfirmDialog";
import toast from "react-hot-toast";

// css is already imported in app.css
config.autoAddCss = false;

const appName = import.meta.env.VITE_APP_NAME || 'Subtitle Extractor';
(window as any).global = window;

if (process.env.NODE_ENV === "production") {
    initSentry();
}

createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) => resolvePageComponent(`./Pages/${name}.tsx`, import.meta.glob('./Pages/**/*.tsx')),
    setup({el, App, props}) {
        hydrateRoot(el,
        <ConfirmProvider>
            <App {...props} />
        </ConfirmProvider>)
    },
}).then(() => {
    axios.interceptors.response.use(
        response => isAuthRedirect(response) ? reloadForExpiredAuth() : response,
        error => {
            const {status} = error.response || {};
            if (authIsExpired(status)) {
                return reloadForExpiredAuth();
            } else {
                return Promise.reject(error);
            }
        }
    );

    router.on('exception', (event) => {
        const { exception } = event.detail;
        if (exception.message === "Network Error") {
            event.preventDefault();
            toast.error("Request failed. Please check your connection and try again.");
        }
    });

    router.on('before', (event) => {
        if (!event.detail.visit.preserveState) {
            toast.remove();
        }
    })
});

function authIsExpired(errorCode: number) {
    return errorCode === 401 || errorCode === 419;
}

function reloadForExpiredAuth() {
    console.error("Auth expired, reloading page")
    window.location.reload();
    return new Promise<never>(() => {});
}

function isAuthRedirect(response: AxiosResponse) {
    const finalUrl = response.request?.responseURL;
    const requestedUrl = response.config?.url;

    if (!finalUrl || !requestedUrl) {
        return false;
    }

    const finalPath = pathOf(finalUrl);

    if (finalPath === pathOf(requestedUrl)) {
        return false;
    }

    return ['login', 'verification.notice'].some(
        name => pathOf(route(name)) === finalPath
    );
}

function pathOf(url: string) {
    return new URL(url, window.location.href).pathname;
}

if (process.env.NODE_ENV === "production") {
    initAnalytics();
}

function initAnalytics() {
    gtag("js", new Date());
    gtag("config", window.analyticsConfig.measurement_id, {
        send_page_view: false
    });
    gtag('config', window.analyticsConfig.ads_id);

    router.on("navigate", (event) => {
        setTimeout(() => {
            const url = event.detail.page.url
                .replace(/(.*\/(?:subtitle|folder))\/[^/]+$/, '$1/:id')

            sendEvent(AnalyticsEvent.PAGE_VIEW, {
                page_location: url,
                page_title: document.title,
            })
        }, 250);  // timeout ensures that the title is updated
    })
}

function initSentry() {
    const tunnel = laravelConfig<string>('services.sentry.tunnel_endpoint');
    Sentry.init({
        dsn: laravelConfig<string>('sentry.dsn'),
        ...(tunnel && { tunnel }),
        enabled: process.env.NODE_ENV === 'production',
        ignoreErrors: [
            "ResizeObserver loop limit exceeded",
            "Non-Error promise rejection captured",
            "Request aborted",
            "Network Error",
            /Hydration failed/i
        ],
        beforeSend(event, hint) {
            if (
                event.exception?.values?.[0]?.stacktrace?.frames?.some((f) =>
                    f.filename?.includes(`googletagmanager.com/gtag`))
            ) {
                return null;
            }
            return event;
        },
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            }),
        ],
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
    });
}
